import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { sellerLabel } from '~/utils';
import { AcceptQuoteButton } from './AcceptQuoteButton';
import { CounterQuoteButton, CounterQuoteForm } from './CounterQuoteAction';
import {
  QuoteConversation,
  type QuoteConversationDrafts,
} from './QuoteConversation';
import { QuoteTimeline } from './QuoteTimeline';
import { RespondForSellerAction } from './RespondForSellerAction';
import {
  buyerQuoteActions,
  type QuoteNegotiationContext,
} from './quoteNegotiation';
import type { InlineSellerResponse } from './SellerQuoteActionSlots';
import { sellerOfferOrderBlock } from './sellerOffer';
import { useSendCounter } from './useSendCounter';
import type { Quote } from './splitAwardGridTypes';

type OpenPanel = 'counter' | 'messages';

type BuyerQuoteActionsProps = Readonly<{
  quote: Quote;
  negotiation: QuoteNegotiationContext;
  conversationDrafts: QuoteConversationDrafts;
  onBuyerResponded?: (quoteId: string) => void;
  sellerResponse?: InlineSellerResponse;
}>;

export function BuyerQuoteActions({
  quote,
  negotiation,
  conversationDrafts,
  onBuyerResponded,
  sellerResponse,
}: BuyerQuoteActionsProps) {
  const { t } = useTranslation('rfq-view');
  const [openPanel, setOpenPanel] = useState<OpenPanel | null>(null);
  const actions = buyerQuoteActions(quote);
  const sellerName = sellerLabel(quote.sellingOrganization);
  const messagesPanelId = `messages-panel-${quote.id}`;

  const togglePanel = (panel: OpenPanel) =>
    setOpenPanel((current) => (current === panel ? null : panel));
  const closePanel = () => setOpenPanel(null);
  const closePanelAfterResponse = () => {
    closePanel();
    onBuyerResponded?.(quote.id);
  };
  const sendCounter = useSendCounter(quote.id, closePanelAfterResponse);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid w-fit grid-cols-2 gap-2">
        {actions.accept !== 'hidden' && (
          <AcceptQuoteButton
            quote={quote}
            disabled={
              actions.accept === 'disabled' ||
              sellerOfferOrderBlock(quote) != null
            }
            onAccepted={onBuyerResponded}
          />
        )}
        {actions.awaitingSeller &&
          (sellerResponse ? (
            <Button
              variant="primary"
              size="sm"
              width="auto"
              data-testid={`submit-for-seller-${quote.id}`}
              aria-label={t('rfq_view.submit_for_seller_aria_label', {
                sellerName,
              })}
              disabled={sellerResponse.isSubmitting}
              loading={sellerResponse.isSubmitting}
              onClick={sellerResponse.submit}
            >
              {t('rfq_view.submit_for_seller_button')}
            </Button>
          ) : (
            <RespondForSellerAction quote={quote} negotiation={negotiation} />
          ))}
        {actions.respondForSeller && (
          <RespondForSellerAction
            quote={quote}
            negotiation={negotiation}
            answersCounter
          />
        )}
        {actions.counter && (
          <CounterQuoteButton
            quoteId={quote.id}
            isOpen={openPanel === 'counter'}
            onToggle={() => togglePanel('counter')}
          />
        )}
        <Button
          variant="secondary"
          size="sm"
          width="auto"
          data-testid={`open-messages-${quote.id}`}
          aria-label={t('rfq_view.messages_aria_label', { sellerName })}
          aria-expanded={openPanel === 'messages'}
          aria-controls={openPanel === 'messages' ? messagesPanelId : undefined}
          onClick={() => togglePanel('messages')}
        >
          {t('rfq_view.messages_button')}
        </Button>
      </div>
      {/* Mounted only while open, so each opening starts from the quote's latest terms. */}
      {openPanel === 'counter' && actions.counter && (
        <CounterQuoteForm
          quote={quote}
          groups={negotiation.groups}
          usesWholeRequestPrice={negotiation.usesWholeRequestPrice}
          requestForQuoteCurrency={negotiation.requestForQuoteCurrency}
          submitCounter={sendCounter}
        />
      )}
      {openPanel === 'messages' && (
        <section
          id={messagesPanelId}
          className="w-full flex flex-col gap-4 border-t pt-3"
          data-testid={messagesPanelId}
          aria-label={t('rfq_view.messages_panel_title', { sellerName })}
        >
          <h3 className="text-sm font-semibold text-neut-prim">
            {t('rfq_view.messages_panel_title', { sellerName })}
          </h3>
          <QuoteTimeline quoteId={quote.id} rounds={quote.rounds} />
          <QuoteConversation
            quoteId={quote.id}
            canComment={actions.comment}
            drafts={conversationDrafts}
          />
        </section>
      )}
    </div>
  );
}
